import { useMemo, useState } from 'react';

import { Content, Header, Text } from '../common/components';
import { useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import routes from '../common/constants/routes';
import { selectSecurity } from '../common/store/securitySlice';
import CarAppBanner from './components/CarAppBanner';
import CategoryChips from './components/CategoryChips';
import InternalServiceCard from './components/InternalServiceCard';
import MemberDivisionCard from './components/MemberDivisionCard';
import PartnerCard from './components/PartnerCard';
import PreviewNotice from './components/PreviewNotice';
import VerifiedPartnerModal from './components/VerifiedPartnerModal';
import { CAR_APP_PARTNER_SLUG, CATEGORY_ORDER } from './config/catalogConfig';
import { useServiceCatalog } from './hooks/useServiceCatalog';
import { ServiceCategory, ServicePartner } from './types';

const namespace = 'OtherServices';

/** Partneři zúžení na nabídky zvolené kategorie; partner bez shody vypadne. */
const filterByCategory = (partners: ServicePartner[], category: ServiceCategory | null): ServicePartner[] => {
  if (category === null) return partners;

  return partners
    .map((partner) => ({ ...partner, offers: partner.offers.filter((offer) => offer.category === category) }))
    .filter((partner) => partner.offers.length > 0);
};

/**
 * Landing katalogu (doc/services-catalog.md §7): úvod + „Co řešíte?" chipy, vlastní služby
 * eVlastníci jako kompaktní tmavé karty (2 vedle sebe) nad katalogem prověřených partnerů.
 * Aplikace eVlastníci Auto je banner mimo filtr – vlastníkům nahoře (je to jejich výhoda),
 * předsedům až pod katalogem. Obsah i role řeší BE – stejná stránka pro obě role.
 */
export const ServiceCatalog: React.FC = () => {
  const isAdmin = useAppSelector(selectSecurity).ui.isAdmin;
  useDocumentTitle(t(isAdmin ? 'adminTitle' : 'memberTitle', namespace));

  const { partners, isLoading, hasError, preview } = useServiceCatalog();
  const [category, setCategory] = useState<ServiceCategory | null>(null);
  const [isVerifiedShown, setIsVerifiedShown] = useState<boolean>(false);

  const carApp = partners.find((partner) => partner.slug === CAR_APP_PARTNER_SLUG) ?? null;
  const catalog = useMemo(() => partners.filter((partner) => partner.slug !== CAR_APP_PARTNER_SLUG), [partners]);

  const categories = useMemo(() => {
    const present = new Set(
      catalog.filter((partner) => partner.kind === 'partner').flatMap((partner) => partner.offers.map((offer) => offer.category))
    );
    return CATEGORY_ORDER.filter((item) => present.has(item));
  }, [catalog]);

  // Chipy „Co řešíte?" filtrují jen katalog partnerů – vlastní služby jsou vidět vždy.
  const internal = useMemo(() => catalog.filter((partner) => partner.kind === 'internal'), [catalog]);
  const externalAll = useMemo(() => catalog.filter((partner) => partner.kind === 'partner'), [catalog]);
  const external = useMemo(() => filterByCategory(externalAll, category), [externalAll, category]);
  const hasAnyPartner = externalAll.length > 0;

  const banner = carApp ? <CarAppBanner partner={carApp} preview={preview} /> : null;

  return (
    <>
      <Header title={t(isAdmin ? 'adminTitle' : 'memberTitle', namespace)} customBacklink={routes.poll} />

      <Content className="Content ServiceCatalog" isLoading={isLoading}>
        <PreviewNotice preview={preview} partners={partners} />

        {hasError && (
          <Text as="p" size="body-medium" color="SystemRed">
            {t('loadError', namespace)}
          </Text>
        )}

        {!hasError && partners.length === 0 && !isLoading && (
          <Text as="p" size="body-medium" color="Neutral600">
            {t('emptyState', namespace)}
          </Text>
        )}

        {!isAdmin && banner}

        {/* Předseda: kompaktní tmavé karty divizí. Vlastník: sekce per divize s výkladními kartami služeb. */}
        {internal.length > 0 && isAdmin && (
          <section className="ServiceCatalog__section">
            <div className="ServiceCatalog__sectionHead">
              <Text as="h2" size="h2" color="Neutral800">
                {t('Section.internal', namespace)}
              </Text>
              <Text as="p" size="body-small" color="Neutral600">
                {t('Section.internalText', namespace)}
              </Text>
            </div>
            <div className="ServiceCatalog__internal">
              {internal.map((partner) => (
                <InternalServiceCard key={partner.slug} partner={partner} preview={preview} />
              ))}
            </div>
          </section>
        )}

        {!isAdmin && internal.length > 0 && (
          <section className="ServiceCatalog__section">
            <div className="ServiceCatalog__sectionHead">
              <Text as="h2" size="h2" color="Neutral800">
                {t('Section.internal', namespace)}
              </Text>
              <Text as="p" size="body-small" color="Neutral600">
                {t('Section.internalText', namespace)}
              </Text>
            </div>
            <div className="ServiceCatalog__memberDivisions">
              {internal.map((partner) => (
                <MemberDivisionCard key={partner.slug} partner={partner} preview={preview} />
              ))}
            </div>
          </section>
        )}

        {/* Chipy „Co řešíte?" stojí až nad katalogem partnerů – vlastní služby jsou vždy vidět, chipy filtrují obojí. */}
        <CategoryChips categories={categories} active={category} onChange={setCategory} />

        {category !== null && external.length === 0 && (
          <Text as="p" size="body-medium" color="Neutral600">
            {t('emptyFilter', namespace)}
          </Text>
        )}

        {external.length > 0 && (
          <section className="ServiceCatalog__section">
            <div className="ServiceCatalog__sectionHead">
              <Text as="h2" size="h2" color="Neutral800">
                {t('Section.partners', namespace)}
              </Text>
              <Text as="p" size="body-small" color="Neutral600">
                {t('Section.partnersText', namespace)}{' '}
                {hasAnyPartner && (
                  <button type="button" className="ServiceCatalog__verifiedLink" onClick={() => setIsVerifiedShown(true)}>
                    {t('verifiedLink', namespace)}
                  </button>
                )}
              </Text>
            </div>
            <div className="ServiceCatalog__partners">
              {external.map((partner) => (
                <PartnerCard key={partner.slug} partner={partner} preview={preview} />
              ))}
            </div>
          </section>
        )}

        {isAdmin && banner}
      </Content>

      <VerifiedPartnerModal isShown={isVerifiedShown} onHide={() => setIsVerifiedShown(false)} />
    </>
  );
};

export default ServiceCatalog;
